'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@repo/components';
import ApprovalsTabById from 'src/parts/tabs/ApprovalsTabById';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getApprovalsForEvent } from 'src/api/personal/eventDetail';

interface ApprovalsSearch {
	page?: number;
	sort?: string;
}

export const Route = createFileRoute('/personal_/events/$eventId/approvals/')({
	validateSearch: (search: Record<string, unknown>): ApprovalsSearch => ({
		page: (search.page as number) || undefined,
		sort: search.sort as string | undefined,
	}),
	component: ApprovalsPage,
});

function ApprovalsPage() {
	const { eventId } = Route.useParams();
	const { page, sort } = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();

	const { data: approvalsData, isLoading: approvalsLoading } = useQuery({
		queryKey: ['personal-event-approvals', eventId, page, sort],
		queryFn: () => getApprovalsForEvent(eventId, { page, sort }),
	});

	const handleUpdate = () => {
		queryClient.invalidateQueries({ queryKey: ['personal-event', eventId] });
		queryClient.invalidateQueries({ queryKey: ['personal-event-approvals', eventId] });
	};

	const handleReload = () => {
		queryClient.invalidateQueries({ queryKey: ['personal-event-approvals', eventId] });
	};

	const handleApprovalSelected = (id: string) => {
		navigate({
			to: '/personal/events/$eventId/approvals/$taskId',
			params: { eventId, taskId: id },
		});
	};

	const isLoading = authLoading || approvalsLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!authenticatedUser) return null;

	return (
		<ApprovalsTabById
			approvals={approvalsData}
			authenticatedUser={authenticatedUser}
			onUpdate={handleUpdate}
			onReload={handleReload}
			onApprovalSelected={handleApprovalSelected}
		/>
	);
}
